import { blue, grey } from "@faro-lotv/flat-ui";
import ChevronRightSharpIcon from "@mui/icons-material/ChevronRightSharp";
import ExpandMoreSharpIcon from "@mui/icons-material/ExpandMoreSharp";
import { Stack, SvgIconProps, SxProps } from "@mui/material";
import { StackProps } from "@mui/system";
import { CSSProperties, PropsWithChildren, useMemo, type JSX } from "react";
import { NodeApi } from "react-arborist";

const TREE_NODE_COLORS = {
  backgroundSelected: blue[500],
  backgroundSelectedIndirectly: `${grey[500]}40`,
  backgroundHover: `${grey[500]}19`,
  text: grey[800],
  textSelected: "white",
};

/** Fixed node height in pixels. React arborist requires each node to have a fixed height to compute which nodes are visible */
export const TREE_NODE_HEIGHT = 33;

export type TreeNodeProps<T> = {
  /** Data for this node */
  node: NodeApi<T>;

  /**
   * Set to true if the node is selected indirectly, depending on the tree use-case.
   *
   * This will highlight the node in a different color.
   *
   * @default false
   */
  isSelectedIndirectly?: boolean;

  /** True if the node is disabled */
  isDisabled?: boolean;

  /** True if the node can be selected @default true */
  isSelectable?: boolean;

  /**
   * True if the node should expand when it is clicked
   *
   * @default true
   */
  shouldExpandNodeOnClick?: boolean;

  /**
   * Whether the node should be deselected when clicked in its selected state
   */
  shouldDeselectOnClick?: boolean;

  /**
   * Styles to apply to this node
   *
   * Note: Those are passed directly by the arborist's Tree component
   */
  style?: CSSProperties;

  /** Pointer enter event on the tree node */
  onPointerEnter?: StackProps["onPointerEnter"];

  /** Pointer leave event on the tree node */
  onPointerLeave?: StackProps["onPointerLeave"];

  /** Enable collapsing the width of the arrow icon if the element has no children */
  collapseIcon?: boolean;

  /**
   * Optional override for whether this node should be treated as a leaf.
   * If omitted, the value from react-arborist (`node.isLeaf`) is used.
   */
  isLeafOverride?: boolean;
};

/**
 * @returns a styled tree node which can be used as a base for creating a custom tree node
 */
export function TreeNode<T>({
  node,
  isSelectedIndirectly = false,
  isDisabled,
  isSelectable = true,
  shouldExpandNodeOnClick = true,
  shouldDeselectOnClick,
  onPointerEnter,
  onPointerLeave,
  children,
  collapseIcon,
  isLeafOverride,
}: PropsWithChildren<TreeNodeProps<T>>): JSX.Element {
  const backgroundColor = useMemo(() => {
    if (node.isSelected) return TREE_NODE_COLORS.backgroundSelected;
    if (node.willReceiveDrop) return `${grey[500]}3F`;
    if (isSelectedIndirectly) {
      return TREE_NODE_COLORS.backgroundSelectedIndirectly;
    }
  }, [node.isSelected, node.willReceiveDrop, isSelectedIndirectly]);

  const isLeaf = isLeafOverride ?? node.isLeaf;

  return (
    <Stack
      direction="row"
      onClick={(ev) => {
        if (shouldExpandNodeOnClick) {
          node.open();
        }

        if (shouldDeselectOnClick && node.isOnlySelection) {
          node.deselect();
          // prevent node from being selected again
          ev.stopPropagation();
        }

        if (!isSelectable) {
          ev.stopPropagation();
        }
      }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      sx={{
        pl: 1,
        alignItems: "center",
        flexGrow: 1,
        // Need to remain aligned to the rowHeight parameter
        height: `${TREE_NODE_HEIGHT}px`,
        "&:hover": isSelectable
          ? {
              backgroundColor:
                node.isSelected || node.willReceiveDrop
                  ? backgroundColor
                  : TREE_NODE_COLORS.backgroundHover,
              color: node.isSelected
                ? TREE_NODE_COLORS.textSelected
                : TREE_NODE_COLORS.text,
              cursor: "pointer",
            }
          : undefined,
        "&:hover .MuiBadge-badge": isSelectable
          ? {
              // The color rgb(229, 229, 229)  matches the backgroundColor rgba(0, 0, 0, 0.1) for the hover selector above.
              borderColor: backgroundColor ?? "rgb(229, 229, 229)",
            }
          : undefined,
        color: node.isSelected
          ? TREE_NODE_COLORS.textSelected
          : TREE_NODE_COLORS.text,
        backgroundColor: backgroundColor ?? "transparent",
        ".MuiBadge-badge": {
          borderColor: backgroundColor ?? "white",
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          height: "100%",
          alignItems: "center",
          pl: "0.1875em",
          overflow: "hidden",
          flexGrow: 1,
          userSelect: "none",
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <ExpandNodeIcon
          isExpanded={!node.isClosed}
          onClick={(ev) => {
            ev.stopPropagation();
            node.toggle();
          }}
          sx={{
            display: "flex",
            mr: 0.5,
            width: collapseIcon && isLeaf ? "0" : undefined,
            visibility: isLeaf ? "hidden" : "unset",
            fontSize: "1.125em",
            cursor: isLeaf ? undefined : "pointer",
          }}
        />
        {children}
      </Stack>
    </Stack>
  );
}

type ExpandNodeIconProps = {
  /** Boolean to know if the node is expanded or closed */
  isExpanded: boolean;

  /** Optional style to apply to the image */
  sx?: SxProps;

  /** Callback called when the icon is clicked */
  onClick?: SvgIconProps["onClick"];
};

/**
 * This component is required, because by simply rotating, with css, the image will become blurry.
 *
 * @returns an arrow icon. The arrow points to below if the node is expanded, otherwise it points to the right.
 */
function ExpandNodeIcon({
  isExpanded,
  sx,
  onClick,
}: ExpandNodeIconProps): JSX.Element {
  return isExpanded ? (
    <ExpandMoreSharpIcon sx={sx} onClick={onClick} />
  ) : (
    <ChevronRightSharpIcon sx={sx} onClick={onClick} />
  );
}
